import {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, TextField, styled} from '@mui/material'
import {
  StyledSlider,
  SliderContainer,
  InputContainer,
  ErrorText,
  CustomLabelsContainer,
  CustomLabel,
  LabelText
} from './RangeSliderStyles'

// Styled TextField component for range inputs
const StyledTextField = styled(TextField)(({theme}) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    '& fieldset': {
      borderColor: theme.palette.grey[300]
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main
    },
    '&.Mui-error fieldset': {
      borderColor: theme.palette.error.main
    },
    '& input': {
      height: '30px',
      width: '30px',
      padding: '4px',
      fontSize: '10px',
      fontWeight: 500,
      textAlign: 'center'
    }
  }
}))

/**
 * RangeSlider - Independent range slider component
 *
 * A standalone, reusable range slider with input boxes and validation.
 * Can be used anywhere in your application without FilterHeader dependency.
 *
 * @param {Object} props
 * @param {number} props.min - Minimum value for the slider range
 * @param {number} props.max - Maximum value for the slider range
 * @param {Array<number>} props.value - Current [min, max] values
 * @param {Function} props.onChange - Callback when slider changes (event, newValue)
 * @param {Function} props.onChangeCommitted - Callback when change is committed
 * @param {boolean} props.useGradient - Whether to use gradient color
 * @param {number} props.step - Step value for slider (default: 1)
 * @param {boolean} props.showInputs - Show input boxes (default: true)
 * @param {string} props.ariaLabel - Aria label for accessibility
 * @param {string} props.minLabel - Custom label for minimum value (e.g., "Lower Risk")
 * @param {string} props.maxLabel - Custom label for maximum value (e.g., "Higher Risk")
 * @param {boolean} props.showCustomLabels - Show custom labels below slider (default: false)
 */
const RangeSlider = ({
  min = 0,
  max = 100,
  value = [0, 100],
  onChange,
  onChangeCommitted,
  useGradient = false,
  step = 1,
  showInputs = true,
  ariaLabel = 'Range slider',
  minLabel = '',
  maxLabel = '',
  showCustomLabels = false,
  ...rest
}) => {
  // Ensure value array doesn't contain null (safety check)
  const safeValue = [value[0] ?? min, value[1] ?? max]

  const [localValue, setLocalValue] = useState(safeValue)
  const [minInput, setMinInput] = useState(safeValue[0].toString())
  const [maxInput, setMaxInput] = useState(safeValue[1].toString())
  const [error, setError] = useState({hasError: false, message: '', field: ''})
  const [validationTimeout, setValidationTimeout] = useState(null)

  // Update local state when external value changes
  useEffect(() => {
    const newSafeValue = [value[0] ?? min, value[1] ?? max]
    setLocalValue(newSafeValue)
    setMinInput(newSafeValue[0].toString())
    setMaxInput(newSafeValue[1].toString())
  }, [value, min, max])

  // Handle slider change
  const handleSliderChange = (event, newValue) => {
    setLocalValue(newValue)
    setMinInput(newValue[0].toString())
    setMaxInput(newValue[1].toString())
    setError({hasError: false, message: '', field: ''})

    if (onChange) {
      onChange(event, newValue)
    }
  }

  // Handle slider change committed
  const handleSliderChangeCommitted = (event, newValue) => {
    if (onChangeCommitted) {
      onChangeCommitted(event, newValue)
    }
  }

  // Validate input values
  const validateInput = (minVal, maxVal, changedField) => {
    const minNum = Number.parseFloat(minVal)
    const maxNum = Number.parseFloat(maxVal)

    if (minVal === '' || maxVal === '') {
      let field = 'max'
      if (minVal === '' && maxVal === '') {
        field = 'both'
      } else if (minVal === '') {
        field = 'min'
      }
      return {
        hasError: true,
        message: 'Both values are required',
        field
      }
    }

    if (Number.isNaN(minNum) || Number.isNaN(maxNum)) {
      return {
        hasError: true,
        message: 'Please enter valid numbers',
        field: Number.isNaN(minNum) ? 'min' : 'max'
      }
    }

    if (minNum < min) {
      return {
        hasError: true,
        message: `Minimum value cannot be less than ${min}`,
        field: 'min'
      }
    }

    if (maxNum > max) {
      return {
        hasError: true,
        message: `Maximum value cannot be greater than ${max}`,
        field: 'max'
      }
    }

    if (minNum > maxNum) {
      return {
        hasError: true,
        message: 'Minimum value cannot be greater than maximum value',
        field: changedField
      }
    }

    return {hasError: false, message: '', field: ''}
  }

  // Handle min input change
  const handleMinInputChange = (event) => {
    const newMinValue = event.target.value

    // Validate numeric input (safer than regex to prevent ReDoS)
    if (newMinValue !== '') {
      const isValidNumber =
        newMinValue === '-' ||
        newMinValue === '.' ||
        newMinValue === '-.' ||
        !Number.isNaN(Number.parseFloat(newMinValue))
      if (!isValidNumber || newMinValue.length > 20) {
        return
      }
    }

    setMinInput(newMinValue)

    if (validationTimeout) {
      clearTimeout(validationTimeout)
    }

    const timeout = setTimeout(() => {
      const validationResult = validateInput(newMinValue, maxInput, 'min')
      setError(validationResult)

      if (!validationResult.hasError) {
        const newValue = [Number.parseFloat(newMinValue), localValue[1]]
        setLocalValue(newValue)
        if (onChangeCommitted) {
          onChangeCommitted(event, newValue)
        }
      }
    }, 600)

    setValidationTimeout(timeout)
  }

  // Handle max input change
  const handleMaxInputChange = (event) => {
    const newMaxValue = event.target.value

    // Validate numeric input (safer than regex to prevent ReDoS)
    if (newMaxValue !== '') {
      const isValidNumber =
        newMaxValue === '-' ||
        newMaxValue === '.' ||
        newMaxValue === '-.' ||
        !Number.isNaN(Number.parseFloat(newMaxValue))
      if (!isValidNumber || newMaxValue.length > 20) {
        return
      }
    }

    setMaxInput(newMaxValue)

    if (validationTimeout) {
      clearTimeout(validationTimeout)
    }

    const timeout = setTimeout(() => {
      const validationResult = validateInput(minInput, newMaxValue, 'max')
      setError(validationResult)

      if (!validationResult.hasError) {
        const newValue = [localValue[0], Number.parseFloat(newMaxValue)]
        setLocalValue(newValue)
        if (onChangeCommitted) {
          onChangeCommitted(event, newValue)
        }
      }
    }, 600)

    setValidationTimeout(timeout)
  }

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout)
      }
    }
  }, [validationTimeout])

  return (
    <Box width='100%'>
      {/* Input Boxes and Slider */}
      {showInputs ? (
        <InputContainer>
          <StyledTextField
            size='small'
            value={minInput}
            onChange={handleMinInputChange}
            error={error.hasError && (error.field === 'min' || error.field === 'both')}
            variant='outlined'
            inputProps={{
              'aria-label': 'Minimum value input'
            }}
          />
          <SliderContainer>
            <StyledSlider
              value={localValue}
              onChange={handleSliderChange}
              onChangeCommitted={handleSliderChangeCommitted}
              min={min}
              max={max}
              step={step}
              valueLabelDisplay='auto'
              useGradient={useGradient}
              aria-labelledby={ariaLabel}
              getAriaLabel={(index) => (index === 0 ? 'Minimum value' : 'Maximum value')}
              getAriaValueText={(value) => `${value}`}
              {...rest}
            />
          </SliderContainer>

          <StyledTextField
            size='small'
            value={maxInput}
            onChange={handleMaxInputChange}
            error={error.hasError && (error.field === 'max' || error.field === 'both')}
            variant='outlined'
            inputProps={{
              'aria-label': 'Maximum value input'
            }}
          />
        </InputContainer>
      ) : (
        <Box px={2}>
          <StyledSlider
            value={localValue}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            min={min}
            max={max}
            step={step}
            valueLabelDisplay='auto'
            useGradient={useGradient}
            aria-labelledby={ariaLabel}
            getAriaLabel={(index) => (index === 0 ? 'Minimum value' : 'Maximum value')}
            getAriaValueText={(value) => `${value}`}
            {...rest}
          />
        </Box>
      )}

      {/* Custom Labels Below Slider */}
      {showCustomLabels && (minLabel || maxLabel) && (
        <CustomLabelsContainer>
          <CustomLabel>{minLabel && <LabelText>{minLabel}</LabelText>}</CustomLabel>
          <CustomLabel>{maxLabel && <LabelText>{maxLabel}</LabelText>}</CustomLabel>
        </CustomLabelsContainer>
      )}

      {/* Error Message */}
      {error.hasError && (
        <ErrorText role='alert' aria-live='polite'>
          Error: {error.message}
        </ErrorText>
      )}
    </Box>
  )
}

RangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func,
  onChangeCommitted: PropTypes.func,
  useGradient: PropTypes.bool,
  step: PropTypes.number,
  showInputs: PropTypes.bool,
  ariaLabel: PropTypes.string,
  minLabel: PropTypes.string,
  maxLabel: PropTypes.string,
  showCustomLabels: PropTypes.bool
}

export default RangeSlider
